import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState } from "@/components/States";
import { toast } from "sonner";
import { CalendarDays, Info, X, Sparkles, Clock, Users, Ban, ArrowLeft } from "lucide-react";

const brl = (n) => Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const input = "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors [color-scheme:dark]";
const STATUS = { pendente: "text-yellow-400", confirmada: "text-emerald-400", recusada: "text-red-400", cancelada: "text-muted-foreground" };

function InfoModal({ space, onClose }) {
  const sched = space.schedule || {};
  const DAYS = [["seg", "Seg"], ["ter", "Ter"], ["qua", "Qua"], ["qui", "Qui"], ["sex", "Sex"], ["sab", "Sáb"], ["dom", "Dom"]];
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full sm:max-w-md glass-strong border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 float-in pb-safe max-h-[85dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <p className="font-display font-bold text-lg flex items-center gap-2">{space.icon} {space.name}</p>
          <button onClick={onClose} data-testid="info-close"><X className="w-5 h-5" /></button>
        </div>
        {space.description && <p className="text-sm text-muted-foreground mb-3">{space.description}</p>}
        {space.location && <p className="text-xs text-muted-foreground mb-3">📍 {space.location}</p>}
        <p className="text-xs uppercase tracking-wider text-primary font-medium mb-2">Horários</p>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          {DAYS.map(([k, lbl]) => {
            const d = sched[k] || {};
            return (
              <div key={k} className="text-xs flex justify-between rounded-lg bg-white/5 px-2.5 py-1.5">
                <span className="text-muted-foreground">{lbl}</span>
                <span>{d.active === false ? "Fechado" : `${d.open || "08:00"}–${d.close || "22:00"}`}</span>
              </div>
            );
          })}
        </div>
        {space.capacity ? <p className="text-sm mb-2"><span className="text-muted-foreground">Capacidade:</span> {space.capacity} pessoas</p> : null}
        {(space.regulation_text || "") && (
          <>
            <p className="text-xs uppercase tracking-wider text-primary font-medium mb-1">Regras</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{space.regulation_text}</p>
          </>
        )}
      </div>
    </div>
  );
}

function ReserveModal({ space, onClose, onDone }) {
  const qc = useQueryClient();
  const [date, setDate] = useState("");
  const [guests, setGuests] = useState(0);
  const [selected, setSelected] = useState(null);

  const { data, isFetching } = useQuery({
    queryKey: ["availability", space.id, date], enabled: !!date,
    queryFn: async () => (await api.get(`/spaces/${space.id}/availability`, { params: { date } })).data,
  });
  const slots = data?.slots || [];
  const paid = space.pricing?.mode === "paid";

  const reserve = async () => {
    if (!selected) return;
    try {
      await api.post("/reservations", { space_id: space.id, date, slot_key: selected.key, guests: Number(guests) || 0 });
      toast.success("Reserva confirmada!");
      qc.invalidateQueries({ queryKey: ["reservations"] });
      qc.invalidateQueries({ queryKey: ["home"] });
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  const waitlist = async (slot) => {
    try {
      const { data: r } = await api.post("/waitlist", { space_id: space.id, date, slot_key: slot.key });
      toast.success(`Você entrou na fila de espera. Posição: ${r.position}º`);
      qc.invalidateQueries({ queryKey: ["waitlist"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full sm:max-w-md glass-strong border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 float-in pb-safe max-h-[88dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display font-bold text-lg flex items-center gap-2">{space.icon} {space.name}</p>
          <button onClick={onClose} data-testid="reserve-close"><X className="w-5 h-5" /></button>
        </div>
        {paid && <p className="text-sm mb-3"><span className="text-muted-foreground">Taxa:</span> <span className="font-semibold text-emerald-400">{brl(space.pricing.fee)}</span>{space.pricing.deposit ? ` · Caução ${brl(space.pricing.deposit)}` : ""}</p>}

        <label className="text-xs text-muted-foreground">Quando?</label>
        <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setSelected(null); }} data-testid="reserve-date" className={`${input} mt-1 mb-4`} />

        {space.reservation_type !== "slots" && (
          <>
            <label className="text-xs text-muted-foreground">Nº de convidados (opcional)</label>
            <input type="number" min="0" value={guests} onChange={(e) => setGuests(e.target.value)} data-testid="reserve-guests" className={`${input} mt-1 mb-4`} />
          </>
        )}

        {date && (
          <>
            <p className="text-xs uppercase tracking-wider text-primary font-medium mb-2">Horários disponíveis</p>
            {isFetching ? <Loading label="Verificando disponibilidade..." /> : slots.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">Sem horários neste dia. Tente outra data.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 mb-4">
                {slots.map((s) => {
                  const full = s.available <= 0;
                  const isSel = selected?.key === s.key;
                  return (
                    <button
                      key={s.key}
                      data-testid="slot-option"
                      onClick={() => (full ? waitlist(s) : setSelected(s))}
                      className={`rounded-2xl p-3 border text-left transition-colors ${
                        full ? "border-red-500/25 bg-red-500/5" : isSel ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5 hover:border-primary/40"
                      }`}
                    >
                      <p className="text-xs font-semibold">{s.label}</p>
                      {space.reservation_type === "slots" ? (
                        <p className={`text-[11px] mt-0.5 ${full ? "text-red-400" : "text-emerald-400"}`}>{full ? "🔴 Sem vagas" : `${s.available} de ${s.capacity} vagas`}</p>
                      ) : (
                        <p className={`text-[11px] mt-0.5 ${full ? "text-red-400" : "text-emerald-400"}`}>{full ? "🔴 Fila de espera" : "🟢 Disponível"}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {selected && (
          <button onClick={reserve} data-testid="confirm-reservation" className="w-full rounded-full brand-gradient py-3 text-white font-semibold active:scale-95 transition-transform">
            Reservar agora {paid && `· ${brl(space.pricing.fee)}`}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ReservasView() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("reservar"); // reservar | minhas
  const [info, setInfo] = useState(null);
  const [reserveSpace, setReserveSpace] = useState(null);

  const { data: spaces, isLoading } = useQuery({ queryKey: ["spaces"], queryFn: async () => (await api.get("/spaces")).data });
  const { data: reservations } = useQuery({ queryKey: ["reservations"], queryFn: async () => (await api.get("/reservations")).data });
  const { data: waitlist } = useQuery({ queryKey: ["waitlist"], queryFn: async () => (await api.get("/waitlist")).data });

  const free = (spaces || []).filter((s) => !s.reservable);
  const reservable = (spaces || []).filter((s) => s.reservable);
  const upcoming = (reservations || []).filter((r) => r.status === "confirmada");
  const history = (reservations || []).filter((r) => r.status !== "confirmada");

  const cancel = async (id) => {
    try { await api.post(`/reservations/${id}/cancel`); qc.invalidateQueries({ queryKey: ["reservations"] }); qc.invalidateQueries({ queryKey: ["home"] }); toast.success("Reserva cancelada."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const leaveWaitlist = async (id) => {
    try { await api.delete(`/waitlist/${id}`); qc.invalidateQueries({ queryKey: ["waitlist"] }); toast.success("Você saiu da fila."); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 glass rounded-full p-1">
        <button onClick={() => setTab("reservar")} data-testid="res-tab-reservar" className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${tab === "reservar" ? "brand-gradient text-white" : "text-muted-foreground"}`}>Reservar</button>
        <button onClick={() => setTab("minhas")} data-testid="res-tab-minhas" className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${tab === "minhas" ? "brand-gradient text-white" : "text-muted-foreground"}`}>Minhas reservas</button>
      </div>

      {tab === "reservar" ? (
        <>
          {reservable.length > 0 && (
            <section>
              <p className="text-xs uppercase tracking-[0.15em] text-primary font-medium mb-2">Espaços reserváveis</p>
              <div className="grid gap-3">
                {reservable.map((s) => (
                  <div key={s.id} data-testid="reservable-space" className="glass rounded-3xl p-4 card-hover">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{s.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-semibold text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {s.reservation_type === "hourly" ? "Por horário" : s.reservation_type === "slots" ? `Por vagas · ${s.slot_capacity} lugares` : s.reservation_type === "full_day" ? "Dia inteiro" : "Por período"}
                          {s.pricing?.mode === "paid" ? ` · ${brl(s.pricing.fee)}` : " · Gratuito"}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setReserveSpace(s)} data-testid="open-reserve" className="flex-1 rounded-full brand-gradient py-2 text-xs font-semibold text-white active:scale-95 transition-transform">Reservar</button>
                      <button onClick={() => setInfo(s)} data-testid="open-info" className="rounded-full bg-white/5 border border-white/10 px-4 py-2 text-xs font-medium hover:bg-white/10 transition-colors flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Regras</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {free.length > 0 && (
            <section>
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2 mt-2">Áreas de uso livre</p>
              <div className="grid gap-3">
                {free.map((s) => {
                  const anyDay = Object.values(s.schedule || {}).find((d) => d.active !== false) || {};
                  return (
                    <div key={s.id} data-testid="free-space" className="glass rounded-3xl p-4 flex items-center gap-3">
                      <span className="text-2xl">{s.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-semibold text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground">Horário: {anyDay.open || "08:00"} às {anyDay.close || "22:00"} · Uso livre</p>
                      </div>
                      <button onClick={() => setInfo(s)} data-testid="free-info" className="rounded-full bg-white/5 border border-white/10 px-3 py-2 text-xs font-medium hover:bg-white/10 transition-colors">Ver informações</button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {reservable.length === 0 && free.length === 0 && <EmptyState icon={CalendarDays} title="Nenhum espaço cadastrado" subtitle="O síndico ainda não cadastrou áreas comuns." />}
        </>
      ) : (
        <div className="space-y-5">
          <section>
            <p className="text-xs uppercase tracking-[0.15em] text-primary font-medium mb-2">Próximas</p>
            {upcoming.length === 0 ? <p className="text-sm text-muted-foreground">Você não tem reservas confirmadas.</p> : (
              <div className="grid gap-3">
                {upcoming.map((r) => (
                  <div key={r.id} data-testid="my-reservation" className="glass rounded-3xl p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{r.space_icon}</span>
                      <div className="flex-1">
                        <p className="font-display font-semibold text-sm">{r.space_name}</p>
                        <p className="text-xs text-muted-foreground">{new Date(r.date).toLocaleDateString("pt-BR")} · {r.slot_label}</p>
                      </div>
                      <span className="text-xs text-emerald-400 font-medium">🟢 Confirmada</span>
                    </div>
                    <button onClick={() => cancel(r.id)} data-testid="cancel-reservation" className="mt-3 w-full rounded-full bg-white/5 border border-white/10 py-2 text-xs font-medium text-red-400 hover:bg-red-500/10 transition-colors">Cancelar reserva</button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {waitlist?.length > 0 && (
            <section>
              <p className="text-xs uppercase tracking-[0.15em] text-yellow-400 font-medium mb-2">Fila de espera</p>
              <div className="grid gap-3">
                {waitlist.map((w) => (
                  <div key={w.id} data-testid="my-waitlist" className="glass rounded-3xl p-4 flex items-center gap-3">
                    <span className="text-2xl">{w.space_icon}</span>
                    <div className="flex-1">
                      <p className="font-display font-semibold text-sm">{w.space_name}</p>
                      <p className="text-xs text-muted-foreground">{new Date(w.date).toLocaleDateString("pt-BR")} · Posição: <span className="text-yellow-400 font-semibold">{w.position}º</span></p>
                    </div>
                    <button onClick={() => leaveWaitlist(w.id)} data-testid="leave-waitlist" className="rounded-full bg-white/5 border border-white/10 px-3 py-2 text-xs font-medium hover:bg-white/10 transition-colors">Sair da fila</button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {history.length > 0 && (
            <section>
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Histórico</p>
              <div className="grid gap-2">
                {history.map((r) => (
                  <div key={r.id} className="glass rounded-2xl p-3 flex items-center gap-3 opacity-70">
                    <span className="text-lg">{r.space_icon}</span>
                    <div className="flex-1"><p className="text-sm">{r.space_name}</p><p className="text-xs text-muted-foreground">{new Date(r.date).toLocaleDateString("pt-BR")} · {r.slot_label}</p></div>
                    <span className={`text-xs capitalize ${STATUS[r.status]}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {info && <InfoModal space={info} onClose={() => setInfo(null)} />}
      {reserveSpace && <ReserveModal space={reserveSpace} onClose={() => setReserveSpace(null)} onDone={() => setReserveSpace(null)} />}
    </div>
  );
}
